<template>
  <v-page-content>
    <div v-if="!isLoading && content && content.contentObject">
      <div>
        <div
          class="headerButtons"
          v-if="this.$route.path.includes('education')"
        >
          <b-button
            v-if="hasRole(roles_.teacher)"
            class="mr-2 mb-2 float-right"
            variant="primary"
            @click="$router.push(`./${$route.params.trainingId}/progress`)"
            :disabled="
              content && content.contentObject.status !== 'опубликовано'
            "
          >
            Прогресс учеников
          </b-button>
          <b-button
            class="mr-2 mb-2 float-right"
            variant="primary"
            v-b-modal.soloTrainingModal
            :disabled="content && !content.contentObject"
          >
            Индивидуальное обучение
          </b-button>
          <b-button
            class="mr-2 mb-2 float-right"
            variant="primary"
            @click="onGroupTrainingClicked"
            :disabled="content && !content.contentObject"
          >
            Обучение в группе
          </b-button>
        </div>
      </div>
      <b-alert v-if="!isLoading" class="mt-2" variant="primary" :show="resourcesLoading">
        <div class="d-flex align-items-center">
          <b-spinner class="mr-3" variant="primary" />
          <strong>Загрузка ресурсов...</strong>
        </div>
      </b-alert>
      <div v-if="editMode && !isLoading" class="d-flex justify-content-end">
        <!-- Кнопки статус / удалить / сохранить / редактировать -->
        <b-button-toolbar class="mb-2">
          <b-button-group>
            <b-button variant="primary" :pressed="true" class="mr-2">
              <em class="fas fa-info" /> Статус: {{ status }}
            </b-button>
            <error-list-button class="mr-2" />
            <b-button
              variant="outline-primary"
              class="mr-2"
              @click="onChangeCode"
              v-if="isEditMode"
            >
              <em class="fas fa-1x fa-code"></em> Изменить код
            </b-button>
            <b-button
              variant="outline-primary"
              :disabled="isRemoved"
              class="mr-2"
              v-b-modal.my-modal.delete-confirmation-modal
            >
              <em class="fa fa-1x fa-times"></em> Удалить
            </b-button>
            <b-button
              variant="outline-primary"
              @click="onSaveClick()"
              :disabled="!canSave
                || content.contentObject.title === null || content.contentObject.title.length === 0"
              class="mr-2"
            >
              <em class="far fa-1x fa-save"></em> Сохранить
            </b-button>
            <b-button
              variant="outline-primary"
              @click="onEditMode()"
              class="mr-2"
              :disabled="isWaiting"
            >
              <em :class="isEditMode ? 'far fa-edit' : 'fas fa-pen'" />
              {{ isEditMode ? 'Просмотр' : 'Редактировать' }}
            </b-button>
            <b-button variant="outline-primary" @click="onClose">
              <em class="fas fa1-x fa-times-circle" /> Закрыть
            </b-button>
          </b-button-group>
        </b-button-toolbar>
        <modal-delete-confirmation :deleteHandler="onDeleteTraining" />
      </div>
      <div v-if="!isEditMode">
        <div v-if="!hasErrors">
          <h2 class="mt-3">
            {{content.contentObject.title ? content.contentObject.title : 'Название отсутствует'}}
          </h2>
          <b-row no-gutters>
            <b-col md="4" sm="12" class="img-col">
              <b-card-img width="300" :src="avatar" class="rounded-0" />
            </b-col>
            <b-col md="8" sm="12">
              <div
                class="text-justify pl-2"
                v-html="content.contentObject.shortDescription"
              />
            </b-col>
          </b-row>
          <div v-for="stage in stagesForFilter" :key="stage.id">
            <div v-if="!(stage.stageName) || hasRole(roles_.content)">
              <b-card bg-variant="light" border-variant="primary" class="mt-5">
                <b-card-text>{{ stage.name }}</b-card-text>
              </b-card>
              <!-- Цикл по разделам -->
              <div
                v-for="(role, index) in getStageRoles(stage)"
                :key="index"
                class="pl-5"
              >
                <b-card variant="outline-secondary">
                  <b-row no-gutters>
                    <b-col cols="11">
                      <b-card-text>{{ role }}</b-card-text>
                    </b-col>
                  </b-row>
                </b-card>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <b-card class="text-center mt-3">
            <em
              class="fas fa-exclamation-triangle text-primary"
              style="font-size: 170px"
            ></em>
            <h3 class="mt-3">
              В тренажере содержатся ошибки
            </h3>
          </b-card>
        </div>
      </div>
      <div v-else-if="isEditMode">
        <b-alert class="mt-2" :show="hasErrors" variant="danger">
          <em class="fas fa-exclamation-triangle"></em>
          В тренажере содержатся ошибки. Редактирование через формы недоступно.
        </b-alert>
        <h4>Название</h4>
        <b-form-input
          :value="content.contentObject.title"
          placeholder="Введите название"
          @input="onTitleChange"
        />
        <b-form-invalid-feedback
          :force-show="isEditMode ? content.contentObject.title === null : false"
        >
          Заголовок не может быть пустым
        </b-form-invalid-feedback>
        <div class="mt-3">
          <img
            id="avatar-image"
            :src="avatar"
            @click="onChangeAvatar"
            @error="$event.target.src = defaultImage"
            alt="Аватар"
            class="mb-3"
            height="190px"
            width="300px"
          />
          <b-button
            @click="onDeleteAvatar"
            v-b-tooltip.hover
            title="Удалить аватар"
            variant="primary"
            class="fa fa-times position-relative"
            style="top: -80px; right: 45px;"
          />
          <b-button
            @click="onChangeAvatar"
            v-b-tooltip.hover
            title="Изменить аватар. Поддерживаемые форматы: .jpg, .png"
            variant="primary"
            class="fa fa-edit position-relative"
            style="top: -80px; right: 130px;"
          />
        </div>
        <h4>Краткое описание</h4>
        <wysiwyg-text-editor dataType="shortDescription" />
        <div v-if="!hasErrors">
          <b-tabs card>
            <b-tab title="Этапы">
              <b-button
                variant="outline-primary"
                class="mr-2"
                v-b-modal.addingStageModal
              >
                <em class="fas fa-plus mr-1" /> Добавить этап
              </b-button>
              <b-button
                variant="outline-primary"
                class="mr-2"
                v-b-modal.resourceModal
              >
                Ресурсы
              </b-button>
              <b-button
                variant="outline-primary"
                class="mr-2"
                v-b-modal.rolesModal
              >
                Роли
              </b-button>
              <div v-for="(stage, index) in stagesForFilter" :key="stage.id">
                <div v-if="!(stage.stageName) || hasRole(roles_.content)">
                  <b-card
                    bg-variant="light"
                    border-variant="primary"
                    class="mt-5"
                  >
                    <b-input-group>
                      <b-input-group-prepend
                        v-if="stage.stageName"
                      >
                        <b-input-group-text v-b-tooltip title="Нештатная ситуация">
                          <em class="fas fa-bomb text-primary"></em>
                        </b-input-group-text>
                      </b-input-group-prepend>
                      <b-form-input
                        :value="stage.name"
                        placeholder="Введите название"
                        @input="onStepTitleChange(stage.id, $event)"
                      />
                      <b-input-group-append>
                        <b-button
                          variant="outline-primary"
                          :disabled="!canMoveStage(index, 'up')"
                          @click="onMoveStage(index, 'up')"
                        >
                          <em class="fas fa-arrow-up" />
                        </b-button>
                        <b-button
                          variant="outline-primary"
                          :disabled="!canMoveStage(index, 'down')"
                          @click="onMoveStage(index, 'down')"
                        >
                          <em class="fas fa-arrow-down" />
                        </b-button>
                        <b-button
                          variant="primary"
                          v-b-tooltip.hover
                          :title="getPreviewTooltip(stage)"
                          @click="onCheckStage(index)"
                        >
                          <em class="fas fa-search" v-if="stage.steps.length > 0" />
                          <em class="fas fa-pencil-alt" v-else />
                        </b-button>
                        <b-button
                          variant="primary"
                          v-b-tooltip.hover
                          title="Удалить этап"
                          @click="onDeleteStage(index)"
                        >
                          <em class="fas fa-times" />
                        </b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </b-card>
                <!-- Цикл по ролям -->
                  <div
                    v-for="(roleOnPanel, roleIndex) in stage.rolesOnPanel"
                    :key="roleIndex"
                    class="pl-5"
                  >
                    <b-card variant="outline-secondary">
                      <b-row no-gutters>
                        <b-col cols="5">
                          <span>Роль: </span>
                          <b-input v-model="roleOnPanel.role" disabled></b-input>
                        </b-col>
                        <b-col cols="1"></b-col>
                        <b-col cols="5">
                          <span>Пульт: </span>
                          <b-input
                            disabled
                            :value="getPanelNameById(roleOnPanel.panel)"
                          ></b-input>
                        </b-col>
                        <b-col cols="1" class="text-center">
                          <b-button
                            variant="primary"
                            class="mt-4"
                            @click="deleteRolePanel(stage, roleOnPanel)"
                          >
                            <em class="fas fa-lg fa-times mt-2"></em>
                          </b-button>
                        </b-col>
                      </b-row>
                    </b-card>
                  </div>
                  <div class="pl-5">
                    <b-card variant="outline-secondary">
                      <b-row no-gutters>
                        <b-col cols="5">
                          <span>Роль: </span>
                          <b-form-select
                            v-model="selectedRoleForAddingToStage[index]"
                            :options="rolesForAddingToStage"
                          ></b-form-select>
                        </b-col>
                        <b-col cols="1"></b-col>
                        <b-col cols="5">
                          <span>Пульт: </span>
                          <b-form-select
                            v-model="selectedPanelForAddingToStage[index]"
                            :options="panelsForAddingToStage(stage)"
                          ></b-form-select>
                        </b-col>
                        <b-col cols="1" class="text-center">
                          <b-button
                            variant="primary"
                            class="mt-4"
                            @click="addRolePanel(stage, index)"
                            :disabled="
                              selectedRoleForAddingToStage[index] === undefined ||
                                selectedPanelForAddingToStage[index] === undefined
                            "
                          >
                            <em class="fas fa-lg fa-plus mt-2"></em>
                          </b-button>
                        </b-col>
                      </b-row>
                    </b-card>
                  </div>
                </div>
              </div>
            </b-tab>
            <b-tab title="Пульты">
              <b-button
                variant="outline-primary"
                class="mr-2"
                v-b-modal.addPanelModal
              >
                <em class="fas fa-plus mr-1" /> Добавить пульт
              </b-button>
              <div v-for="(panel, index) in panels" :key="panel.id">
                <b-card
                  bg-variant="light"
                  border-variant="primary"
                  class="mt-1"
                >
                  <b-input-group>
                    <b-form-input
                      :value="panel.name"
                      placeholder="Введите название"
                      @input="onPanelTitleChange(index, $event)"
                    />
                    <b-input-group-append>
                      <b-button
                        variant="primary"
                        v-b-tooltip.hover
                        title="Редактировать пульт"
                        @click="onChangePanel(panel.id)"
                      >
                        <em class="fas fa-pen" />
                      </b-button>
                      <b-button
                        variant="primary"
                        v-b-tooltip.hover
                        title="Удалить пульт"
                        @click="onDeletePanel(index)"
                      >
                        <em class="fas fa-times" />
                      </b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-card>
              </div>
            </b-tab>
          </b-tabs>
          <content-accept :beforeSendToVerification="onSaveClick" />
          <v-modal-files-upload
            wrkspcType="avatar"
            :fileHandler="fileHandler"
          />
          <resource-modal />
          <adding-stage-modal />
          <add-panel-modal />
          <roles-modal />
          <set-marks-modal />
          <component-edit-modal />
          <check-stage-modal :stage-index="stageForCheck"></check-stage-modal>
        </div>
      </div>
      <div v-if="!hasErrors">
        <b-modal
          id="soloTrainingModal"
          @show="resetSoloModal"
          @hidden="resetSoloModal"
          hide-footer
          centered
        >
          <template slot="modal-title">
            <div>
              Индивидуальное обучение
            </div>
          </template>
          <div>
            <div>Выберите роль</div>
            <b-form-select
              v-model="selectedRole"
              :options="rolesForSteps"
            ></b-form-select>
            <div>Выберите этап</div>
            <b-form-select
              v-model="selectedStep"
              :options="stepsForRoles"
            ></b-form-select>
          </div>
          <b-row class="mt-4">
            <b-col>
              <b-button
                block
                variant="primary"
                :disabled="!(selectedStep !== null) || !(selectedRole !== null)"
                @click="gotoSoloEducation()"
              >
                Обучение
              </b-button>
            </b-col>
            <b-col>
              <b-button
                block
                variant="primary"
                :disabled="!(selectedStep !== null) || !(selectedRole !== null)"
                @click="gotoSoloTesting()"
              >
                Самотестирование
              </b-button>
            </b-col>
          </b-row>
        </b-modal>
        <b-modal
          id="partyTrainingModal"
          no-close-on-esc
          no-close-on-backdrop
          hide-footer
          centered
          @show="onGroupTrainingModalShow"
          @hidden="onGroupTrainingModalHidden"
        >
          <template slot="modal-title">
            <div>
              Обучение в группе
            </div>
          </template>
          <div>
            <div>Роль - {{ roleForParty }}</div>
            <div>№ команды - {{ numberGroupForParty }}</div>
            <div class="text-center">
              <b-spinner
                style="width: 6rem; height: 6rem;"
                variant="primary"
                label="Large Spinner"
              ></b-spinner>
              <div class="mt-2">
                Ожидание начала занятия классным руководителем
              </div>
            </div>
          </div>
          <b-row class="mt-4">
            <b-col> </b-col>
            <b-col>
              <b-button block variant="primary" @click="declinePartyTraining()">
                Отмена
              </b-button>
            </b-col>
          </b-row>
        </b-modal>
      </div>
    </div>
    <div v-else class="d-flex justify-content-center mb-3">
      <b-spinner
        variant="primary"
        style="width: 5cm; height: 5cm;"
        label="Загрузка..."
      ></b-spinner>
    </div>
  </v-page-content>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import _ from 'lodash'

import ErrorListButton from '@/components/content/education/training/tools/ErrorListButton.vue'
import ResourceModal from '@/components/content/education/training/modals/ResourceModal.vue'
import RolesModal from '@/components/content/education/training/modals/RolesModal.vue'
import AddingStageModal from '@/components/content/education/training/modals/AddingStageModal.vue'
import AddPanelModal from '@/components/content/education/training/modals/AddPanelModal.vue'
import SetMarksModal from '@/components/content/education/training/modals/SetMarksModal.vue'
import ComponentEditModal from '@/components/content/education/training/modals/ComponentModal.vue'
import CheckStageModal from '@/components/content/education/training/modals/CheckStageModal.vue'

import ContentAccept from '@/components/content/verification/ContentAccept.vue'
import ModalDeleteConfirmation from '@/components/content/edit/modals/ModalDeleteConfirmation.vue'
import WysiwygTextEditor from '@/components/content/edit/WysiwygTextEditor.vue'
import VModalFilesUpload from '@/components/content/edit/modals/ModalFilesUpload.vue'

import { roles } from '@/config/roles'
import { statusFilters } from '@/config/content/statuses'

export default {
  name: 'TrainingContent',
  props: {
    editMode: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    window.onbeforeunload = false
    this.updateUpdater(0)
    this.fetchTrainingContent(this.$route.params.trainingId).then(() => {
      this.isLoading = false
      this.resourcesLoading = !this.resources.isLoaded
      this.resources.onload = () => {
        this.resourcesLoading = false
        this.validateObjects()
      }
      this.validateObjects()
    })
    this.debouncedValidation = _.debounce(this.validateObjects, 1000)
    this.setEditMode(false)
  },
  components: {
    WysiwygTextEditor,
    ModalDeleteConfirmation,
    VModalFilesUpload,
    ResourceModal,
    RolesModal,
    SetMarksModal,
    ComponentEditModal,
    ContentAccept,
    AddingStageModal,
    AddPanelModal,
    ErrorListButton,
    CheckStageModal
  },
  data() {
    return {
      roles_: roles,
      isLoading: true,
      isWaiting: false, // ?
      isEditMode: false,
      defaultImage: '/images/w1400_1.jpg',
      roleForParty: '',
      numberGroupForParty: null,
      selectedRole: null,
      selectedStep: null,
      selectedRoleForAddingToStage: {},
      selectedPanelForAddingToStage: {},
      stageForCheck: null,
      resourcesLoading: true
    }
  },
  methods: {
    ...mapActions({
      deleteTraining: 'training/deleteTraining',
      fetchTrainingContent: 'training/fetchTrainingContent',
      sendMessage: 'socket/sendMessage',
      updateTrainingContent: 'training/updateTrainingContent',
      updateUpdater: 'contentManager/updateUpdater',
      validateObjects: 'training/validateObjects'
    }),
    ...mapMutations({
      addPanel: 'training/addPanel',
      deleteAvatar: 'content/deleteAvatar',
      deletePanel: 'training/deletePanel',
      objectsToFiles: 'training/objectsToFiles',
      removeConnection: 'socket/removeConnection',
      setAvatarWithoutFiles: 'content/setAvatarWithoutFiles',
      setContentData: 'content/setContentData',
      setEditMode: 'contentManager/editMode',
      setIsFirstCreatingTrainingStage: 'training/setIsFirstCreatingTrainingStage',
      setNewConnection: 'socket/setNewConnection',
      setOnMessage: 'socket/setOnMessage',
      setStepNumberForGroupTraining: 'training/setStepNumberForGroupTraining',
      setTrainingAllRoles: 'training/setTrainingAllRoles',
      setTrainingGroup: 'training/setTrainingGroup',
      setTrainingId: 'training/setTrainingId',
      setTrainingMode: 'training/setTrainingMode',
      setTrainingRole: 'training/setTrainingRole',
      setTrainingSettings: 'training/setTrainingSettings',
      setTrainingStep: 'training/setTrainingStep',
      updateContentSavedStatus: 'contentManager/updateContentSavedStatus',
      updatePanel: 'training/updatePanel',
      updateTitle: 'content/updateTitle'
    }),
    onCheckStage(stageIndex) {
      this.stageForCheck = stageIndex
      if (!this.course.stages[stageIndex].steps.length) {
        this.setIsFirstCreatingTrainingStage(1)
      }
      this.$bvModal.show('checkStageModal')
    },
    deleteRolePanel(stage, roleOnPanel) {
      // eslint-disable-next-line no-param-reassign
      stage.rolesOnPanel = stage.rolesOnPanel.filter(
        (r) => r.role !== roleOnPanel.role || r.panel !== roleOnPanel.panel,
      )
      this.updateUpdater()
    },
    addRolePanel(stage, index) {
      stage.rolesOnPanel.push({
        role: this.selectedRoleForAddingToStage[index],
        panel: this.selectedPanelForAddingToStage[index]
      })
      this.updateUpdater()
    },
    getPanelNameById(panelId) {
      let name = panelId
      this.panels.forEach((panel) => {
        if (panel.id === panelId) {
          name = panel.name
        }
      })
      return name
    },
    onMoveStage(index, direction) {
      const newIndex = index + (direction === 'up' ? -1 : 1)
      this.course.moveStage(index, newIndex)
      this.updateUpdater()
    },
    canMoveStage(index, direction) {
      if (direction === 'up' && index === 0) {
        return false
      }
      if (direction === 'down' && index === this.course.stages.length - 1) {
        return false
      }
      return true
    },
    onDeleteStage(index) {
      this.course.deleteStage(index)
      this.updateUpdater()
    },
    onClose() {
      this.$router.push('/content/training')
    },
    onPanelTitleChange(index, newTitle) {
      this.updatePanel({ index, newPanelData: { name: newTitle } })
      this.updateUpdater()
    },
    onDeletePanel(index) {
      this.deletePanel(index)
      this.updateUpdater()
    },
    onChangePanel(index) {
      this.$router.push(`/content/training/${this.$route.params.trainingId}/panels/${index}`)
    },
    async onDeleteTraining() {
      await this.deleteTraining(this.$route.params.trainingId)
      this.$router.push('/content/training')
    },
    onChangeAvatar() {
      this.$bvModal.show('files-upload-modal')
    },
    fileHandler(files) {
      this.setAvatarWithoutFiles(files[0])
      this.updateUpdater()
    },
    onDeleteAvatar() {
      this.deleteAvatar()
      this.updateUpdater()
    },
    onTitleChange(event) {
      this.updateTitle(event)
      this.updateUpdater()
    },
    onChangeCode() {
      this.objectsToFiles()
      this.$router.push(
        `/content/training/${this.$route.params.trainingId}/code`,
      )
    },
    onStepTitleChange(id, event) {
      this.course.stages.some((stage) => {
        if (stage.id === id) {
          stage.name = event
          return true
        }
        return false
      })
      this.updateUpdater()
    },
    onEditMode() {
      this.isEditMode = !this.isEditMode
      this.setEditMode(!this.storeIsEdit)
    },
    async onSaveClick() {
      this.isLoading = true
      await this.updateTrainingContent()
      await this.fetchTrainingContent(this.$route.params.trainingId)
      this.updateUpdater(0)
      this.isLoading = false
      this.isEditMode = false
      window.onbeforeunload = false
      this.setEditMode(false)
    },
    resetSoloModal() {
      this.selectedRole = null
      this.selectedStep = null
    },
    gotoTraining() {},
    gotoSoloEducation() {
      this.setTrainingMode(0)
      this.setTrainingRole(this.selectedRole)
      this.setTrainingStep(
        this.stagesForFilter.find((p) => p.name === this.selectedStep)
      )
      this.$router.push(`./${this.$route.params.trainingId}/stages/${this.course
        .stages.map((s) => s.name).indexOf(this.selectedStep)}`)
    },
    gotoSoloTesting() {
      this.setTrainingMode(1)
      this.setTrainingRole(this.selectedRole)
      this.setTrainingStep(
        this.stagesForFilter.find((p) => p.name === this.selectedStep)
      )
      this.$router.push(`./${this.$route.params.trainingId}/stages/${this.course
        .stages.map((s) => s.name).indexOf(this.selectedStep)}`)
    },
    declinePartyTraining() {
      this.$bvModal.hide('partyTrainingModal')
    },
    getAllRoles() {
      return this.course.roles
    },
    getStageRoles(stage) {
      return Array.from(
        new Set(stage.rolesOnPanel.map((roleOnPanel) => roleOnPanel.role)),
      )
    },
    panelsForAddingToStage(stage) {
      const usedPanels = new Set(
        stage.rolesOnPanel.map((roleOnPanel) => roleOnPanel.panel),
      )
      return this.panels
        .filter((panel) => !usedPanels.has(panel.id))
        .map((panel) => ({
          value: panel.id,
          text: `(${panel.id}) ${panel.name}`
        }))
    },
    onGroupTrainingClicked() {
      if (this.hasRole('classroom-teacher')) {
        this.$router.push(`./${this.$route.params.trainingId}/group-settings`)
      } else {
        this.$bvModal.show('partyTrainingModal')
      }
    },
    onGroupTrainingModalShow() {
      this.setNewConnection()
      const wsSend = () => {
        setTimeout(
          () => {
            if (this.isSocketReady === true) {
              this.setOnMessage((e) => {
                const msg = JSON.parse(e.data)
                switch (msg.action) {
                  case 'trainingRegistrationUpdate': {
                    this.roleForParty = JSON.parse(e.data).data.role
                    this.numberGroupForParty = JSON.parse(e.data).data.group
                    break
                  }
                  case 'startTraining': {
                    this.setTrainingMode(msg.data.type)
                    this.setTrainingRole(this.roleForParty)
                    this.setTrainingGroup(this.numberGroupForParty)
                    this.setTrainingSettings(msg.data.settings)
                    this.setTrainingId(msg.data.trainingId)
                    this.setTrainingAllRoles(msg.data.settings.allRoles[this.numberGroupForParty])
                    const mainStage = {}
                    mainStage.steps = []
                    mainStage.rolesOnPanel = []
                    mainStage.name = ''
                    let stepNumber = 1
                    msg.data.settings.stages.forEach((stageName) => {
                      const stage = this.course.stages.find((s) => s.name === stageName)
                      if (stepNumber === 1) {
                        mainStage.name = stageName
                      } else {
                        mainStage.name += `. ${stageName}`
                      }
                      if (stage.stageName) {
                        const defaultStage = this.course.stages
                          .find((s) => s.name === stage.stageName)
                        defaultStage.steps.forEach((step) => {
                          if (step.number < stage.startStep) {
                            step.number = stepNumber
                            stepNumber += 1
                            mainStage.steps.push(step)
                          }
                        })
                        defaultStage.rolesOnPanel.forEach((roleOnPanel) => {
                          mainStage.rolesOnPanel.push(roleOnPanel)
                        })
                      }
                      stage.steps.forEach((step) => {
                        step.number = stepNumber
                        stepNumber += 1
                        mainStage.steps.push(step)
                      })
                      if (stage.endStep) {
                        const defaultStage = this.course.stages
                          .find((s) => s.name === stage.stageName)
                        if (stage.endStep < defaultStage.steps.length) {
                          defaultStage.steps.forEach((step) => {
                            if (step.number > stage.endStep) {
                              step.number = stepNumber
                              stepNumber += 1
                              mainStage.steps.push(step)
                            }
                          })
                        }
                      }
                      stage.rolesOnPanel.forEach((roleOnPanel) => {
                        mainStage.rolesOnPanel.push(roleOnPanel)
                      })
                    })
                    this.setTrainingStep(mainStage)
                    this.$router.push(`./${this.$route.params.trainingId}/stages/group`)
                    break
                  }
                  case 'trainingStepUpdate': {
                    this.setStepNumberForGroupTraining(msg.data.stepNumber)
                    break
                  }
                  default:
                    break
                }
              })
              this.sendMessage({ action: 'trainingRegistration', data: { role: null } })
            } else {
              // console.log('waiting for connection...')
              wsSend()
            }
          }, 10,
        )
      }

      wsSend()
    },
    onGroupTrainingModalHidden() {
      this.sendMessage({ action: 'close' })
      this.roleForParty = ''
      this.numberGroupForParty = null
    },
    getPreviewTooltip(stage) {
      if (stage.steps.length === 0) {
        return 'Запустить в режиме записи'
      }
      return 'Запустить в режиме просмотра'
    }
  },
  computed: {
    ...mapGetters({
      content: 'training/getTrainingContent',
      course: 'training/getTrainingCourse',
      panels: 'training/getPanels',
      updater: 'contentManager/getUpdater',
      isContentSaved: 'contentManager/isContentSaved',
      errors: 'training/getErrors',
      hasErrors: 'training/hasErrors',
      hasRole: 'currentUser/hasRole',
      isSocketReady: 'socket/isSocketReady',
      resources: 'training/getResources',
      storeIsEdit: 'contentManager/isEditMode'
    }),
    canSave() {
      return !this.isContentSaved && this.content.contentObject.title.length > 0
    },
    rolesForAddingToStage() {
      return this.getAllRoles()
    },
    stagesForFilter() {
      return this.course.stages
    },
    rolesForSteps() {
      let rolesOptions = []
      if (this.selectedStep !== null) {
        rolesOptions = this.getStageRoles(
          this.course.stages.find((stage) => stage.name === this.selectedStep),
        ).map((r) => ({
          value: r,
          text: r
        }))
      } else {
        rolesOptions = this.getAllRoles()
      }
      return rolesOptions
    },
    stepsForRoles() {
      const stepsToReturn = []
      this.stagesForFilter.forEach((stage) => {
        stage.rolesOnPanel.forEach((roleOnPanel) => {
          if (
            this.selectedRole === null
            || this.selectedRole === roleOnPanel.role
          ) {
            if (!stepsToReturn.filter((s) => s.value === stage.name).length) {
              stepsToReturn.push({
                value: stage.name,
                text: stage.name
              })
            }
          }
        })
      })
      return stepsToReturn
    },
    courseId() {
      return this.$route.params.trainingId
    },
    avatar() {
      let avatar = this.defaultImage
      if (
        this.content
        && this.content.contentObject
        && this.content.contentObject.avatar
      ) {
        avatar = `/api/files/${
          this.content.contentObject.avatarFile.physicalName
        }`
      }
      return avatar
    },
    status() {
      if (this.content && this.content.contentObject) {
        return this.content.contentObject.status
      }
      return ''
    },
    isRemoved() {
      return this.status === statusFilters.removed
    },
    isDraft() {
      return this.status === statusFilters.draft
    }
  },
  watch: {
    updater(newUpdater) {
      if (newUpdater > 0) {
        this.updateContentSavedStatus(false)
        window.onbeforeunload = () => 'Имеются несохранённые изменения. Вы точно хотите закрыть страницу?'
      } else {
        this.updateContentSavedStatus(true)
        window.onbeforeunload = undefined
      }
      this.debouncedValidation()
    }
  }
}
</script>

<style scoped>
.headerButtons {
  width: 100%;
  padding: 20px 10px 5px 10px;
}
</style>
